import React from 'react';
import {useTranslation} from 'react-i18next';

import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import styled from 'styled-components';

import {useHotkey} from '@edna/hooks';

import {formField, newFormField} from '../FormField';
import {TValue} from '../TextInput';
import {Stack} from '../primitives';
import {TimeColumn} from './TimeColumn';
import * as S from './style';
import {
  DEFAULT_WIDTH,
  ETimePeriod,
  ETimePickerType,
  MAX_TIME,
  MILLISECONDS,
  MIN_TIME,
  PERIODS,
  TTime,
  TTimePickerProps,
} from './types';
import {formatInputValue, formatTime, getTimeValue, isAvailableTime, isFilledTime} from './utils';

export const TimePicker = React.memo<TTimePickerProps>(
  ({
    type = ETimePickerType.HM,
    innerRef,
    value: propValue,
    name,
    className,
    maxTime: propMaxTime,
    minTime: propMinTime,
    hasAcceptButton,
    isInvalid,
    disabled,
    readOnly,
    onFocus,
    onChange,
    onBlur,
    dataSelector,
    placeholder: propPlaceholder,
  }) => {
    const {t} = useTranslation();

    const value = React.useMemo(() => getTimeValue(type, propValue), [type, propValue]);

    const maxTime = React.useMemo(() => {
      const timeValue = getTimeValue(type, propMaxTime);
      const resultMaxTime = new Date();

      resultMaxTime.setHours(
        timeValue.HOURS ?? (type === ETimePickerType.MS ? MIN_TIME : MAX_TIME.HOURS),
        timeValue.MINUTES ?? MAX_TIME.MINUTES,
        timeValue.SECONDS ?? MAX_TIME.SECONDS,
        MILLISECONDS,
      );

      return resultMaxTime;
    }, [propMaxTime, type]);

    const minTime = React.useMemo(() => {
      const timeValue = getTimeValue(type, propMinTime);
      const resultMinTime = new Date();

      resultMinTime.setHours(
        timeValue.HOURS ?? MIN_TIME,
        timeValue.MINUTES ?? MIN_TIME,
        timeValue.SECONDS ?? MIN_TIME,
        MILLISECONDS,
      );

      return resultMinTime;
    }, [propMinTime, type]);

    const [hoveredTime, setHoveredTime] = React.useState<TTime>({});
    const [selectedTime, setSelectedTime] = React.useState<TTime>(value);
    const [isOpen, setIsOpen] = React.useState(true);
    const [inputValue, setInputValue] = React.useState<string | null>(null);
    const [scrollToSelected, setScrollToSelected] = React.useState<TTime | null>(null);

    React.useEffect(() => {
      setSelectedTime(value);
      setInputValue(null);
      setScrollToSelected(value);
    }, [value]);

    const hasPeriod = React.useMemo(
      () => ({
        [ETimePeriod.HOURS]: [ETimePickerType.HMS, ETimePickerType.HM].includes(
          type as ETimePickerType,
        ),
        [ETimePeriod.MINUTES]: true,
        [ETimePeriod.SECONDS]: [ETimePickerType.HMS, ETimePickerType.MS].includes(
          type as ETimePickerType,
        ),
      }),
      [type],
    );

    const handleReset = React.useCallback(() => {
      setHoveredTime({});
      setSelectedTime(value);
      setInputValue(null);
      setScrollToSelected(null);
    }, [value]);

    const handleMouseEnter = React.useCallback((time: TTime) => setHoveredTime(time), []);

    const handleMouseLeave = () => setHoveredTime({});

    const handleSubmit = React.useCallback(
      (nextTime: TTime = selectedTime, shouldLeaveOpen: boolean = false) => {
        if (
          !isFilledTime(nextTime, type) ||
          !isAvailableTime(nextTime, {minDate: minTime, maxDate: maxTime, type})
        ) {
          return;
        }

        const date = new Date();

        date.setHours(
          nextTime.HOURS ?? date.getHours(),
          nextTime.MINUTES,
          nextTime.SECONDS,
          MILLISECONDS,
        );

        setIsOpen(shouldLeaveOpen);

        onChange?.(formatTime(nextTime, type, t(`EdnaTimePicker:timePlaceholder.${type}`)), date);
      },
      [selectedTime, type, onChange, t, minTime, maxTime],
    );

    useHotkey('Enter', () => handleSubmit());

    const handleSelect = React.useCallback(
      (time: TTime) => {
        setSelectedTime((prev) => {
          const nextTime = {...prev, ...time};

          if (!hasAcceptButton && isFilledTime(nextTime, type)) {
            handleSubmit(nextTime);
          }

          return nextTime;
        });

        setInputValue(null);
        setScrollToSelected(null);
      },
      [handleSubmit, hasAcceptButton, type],
    );

    const handleChange = React.useCallback<React.ChangeEventHandler<HTMLInputElement>>(
      (event) => {
        const valueLength = event.target.value.length;
        const selectionStart = event.target.selectionStart ?? valueLength;

        event.target.value = formatInputValue(type, event.target.value);

        if (
          event.target.value.slice(0, selectionStart).endsWith(':') &&
          valueLength < event.target.value.length
        ) {
          event.target.selectionStart = event.target.selectionEnd = selectionStart + 1;
        } else {
          event.target.selectionStart = event.target.selectionEnd = selectionStart;
        }

        setInputValue(event.target.value);

        const validInputLength = type === ETimePickerType.HMS ? 8 : 5;

        if (event.target.value.length === validInputLength) {
          const nextTime = getTimeValue(type, event.target.value);

          setScrollToSelected(nextTime);
          setSelectedTime(nextTime);

          if (!hasAcceptButton) {
            handleSubmit(nextTime, true);
          }
        }
      },
      [type, hasAcceptButton, handleSubmit],
    );

    const visibleValue = React.useMemo(() => {
      if (inputValue !== null) {
        return inputValue;
      }

      if (!isEmpty(hoveredTime) && !isEqual({...selectedTime, ...hoveredTime}, selectedTime)) {
        return '';
      }

      if (isFilledTime(selectedTime, type)) {
        return formatTime(selectedTime, type, t(`EdnaTimePicker:timePlaceholder.${type}`));
      }

      return '';
    }, [inputValue, hoveredTime, selectedTime, t, type]);

    const handleClose = React.useCallback(() => {
      if (!hasAcceptButton) {
        handleSubmit();
      }

      handleReset();
    }, [hasAcceptButton, handleReset, handleSubmit]);

    return (
      <S.Dropdown
        disabled={disabled}
        readOnly={readOnly}
        closeWhen="clickOutsideDropdown"
        onClose={handleClose}
        onOpen={() => setIsOpen(true)}
        head={
          <S.TextInput
            onClick={() => setIsOpen(true)}
            readOnly={readOnly}
            disabled={disabled}
            isInvalid={isInvalid}
            id={name}
            type={type}
            value={visibleValue}
            onChange={handleChange}
            onFocus={onFocus}
            onBlur={onBlur}
            innerRef={innerRef}
            className={className}
            dataSelector={dataSelector}
            placeholder={
              propPlaceholder ??
              formatTime(
                inputValue !== null ? {} : {...selectedTime, ...hoveredTime},
                type,
                t(`EdnaTimePicker:timePlaceholder.${type}`),
              )
            }
            leftChildren={<S.ClockIcon disabled={disabled} />}
          />
        }
        overlayComponent={S.Overlay}
        overlay={
          isOpen ? (
            <S.Wrapper>
              <Stack direction="ROW">
                {PERIODS.filter((period) => !!hasPeriod[period]).map((period) => (
                  <S.TimeHeader key={period}>{t(`EdnaTimePicker:options.${period}`)}</S.TimeHeader>
                ))}
              </Stack>
              <Stack direction="ROW">
                {PERIODS.filter((period) => !!hasPeriod[period]).map((period) => (
                  <TimeColumn
                    key={period}
                    period={period}
                    scrollToSelected={scrollToSelected}
                    selectedTime={selectedTime}
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}
                    onSelect={handleSelect}
                    minTime={minTime}
                    maxTime={maxTime}
                    type={type}
                  />
                ))}
              </Stack>
              {hasAcceptButton && (
                <S.Button
                  size="S"
                  appearance="primary"
                  onClick={() => handleSubmit()}
                  disabled={
                    !isFilledTime(selectedTime, type) ||
                    !isAvailableTime(selectedTime, {minDate: minTime, maxDate: maxTime, type})
                  }
                >
                  {t(`EdnaTimePicker:acceptButton`)}
                </S.Button>
              )}
            </S.Wrapper>
          ) : (
            <></>
          )
        }
      />
    );
  },
);

const FullWidthTimePicker = styled(TimePicker)`
  width: 100%;
`;

export const TimePickerField = formField<TTimePickerProps, TValue>(FullWidthTimePicker, {
  width: ({type = ETimePickerType.HM}) => DEFAULT_WIDTH[type as ETimePickerType],
});

export const NewTimePickerField = newFormField<TTimePickerProps>(FullWidthTimePicker, {
  width: ({type = ETimePickerType.HM}) => DEFAULT_WIDTH[type as ETimePickerType],
});
